import { Button, Colors, Title } from '@cognite/cogs.js';
import { Icon } from 'components/Icon';
import { useEffect, useState } from 'react';
import CreatableSelect from 'react-select/creatable';
import { TENANT_LOCALSTORAGE_KEY } from 'store/auth';
import styled from 'styled-components/macro';

const ITG_DOMAIN = 'itg.cognite.ai';
const ITG_INTEGRAL_DOMAIN = 'itg.integral.cognite.ai';

const activeDomain =
  [ITG_DOMAIN, ITG_INTEGRAL_DOMAIN].includes(window.location.hostname) &&
  window.location.hostname;

const envSettings: Record<string, EnvSetting[]> = {
  cognite: [
    {
      id: 1,
      name: 'cognite',
      clusterId: '0',
      audience: 'cognitetenant.onmicrosoft.com',
      clusterGroupNumber: 0,
      domains: [ITG_DOMAIN],
    },
    {
      id: 2,
      name: 'itg-testing',
      clusterId: '0',
      audience: 'itgoncognite.onmicrosoft.com',
      clusterGroupNumber: 0,
      domains: [ITG_DOMAIN, ITG_INTEGRAL_DOMAIN],
    },
    {
      id: 7,
      name: 'galactica',
      clusterId: '0',
      audience: 'galacticatenant.onmicrosoft.com',
      clusterGroupNumber: 0,
      domains: [ITG_DOMAIN],
    },
  ],
  'aker-solutions': [
    {
      id: 3,
      name: 'integral',
      clusterId: '1',
      audience: 'akersolutions.onmicrosoft.com',
      clusterGroupNumber: 0,
      domains: [ITG_INTEGRAL_DOMAIN],
    },
    {
      id: 4,
      name: 'integral-test',
      clusterId: '1',
      audience: 'akersolutions.onmicrosoft.com',
      clusterGroupNumber: 0,
      domains: [ITG_DOMAIN],
    },
    {
      id: 5,
      name: 'integral-aize',
      clusterId: '1',
      audience: 'xdtaas.onmicrosoft.com',
      clusterGroupNumber: 0,
      domains: [ITG_DOMAIN],
    },
    {
      id: 6,
      name: 'integral-develop',
      clusterId: '1',
      audience: 'akersolutions.onmicrosoft.com',
      clusterGroupNumber: 0,
      domains: [ITG_DOMAIN],
    },
  ],
};

export interface EnvSetting {
  id: number;
  name: string;
  clusterId: string;
  audience: string;
  clusterGroupNumber: number;
  domains: string[];
}

type Props = {
  isAuthenticated: boolean;
  hasError: boolean;
  isInitializing: boolean;
  move: (project: string) => void;
  logout: () => void;
  onProjectSelect: (envSetting: EnvSetting) => void;
};

const companies = [
  { label: 'Cognite', value: 'cognite' },
  { label: 'Aker Solutions', value: 'aker-solutions' },
].sort((a, b) => (a.label < b.label ? -1 : 1));

export const NEW_LOGIN_STORAGE_KEY = 'new_ui_login';

export function ProjectSelector(props: Props) {
  const [selectedCompany, setSelectedCompany] = useState(companies[0]);
  const [envVariables, setEnvVariables] = useState<EnvSetting[]>([]);
  const handleSelectingProject = (env: EnvSetting) => {
    localStorage.setItem(NEW_LOGIN_STORAGE_KEY, JSON.stringify(env));
    props.onProjectSelect(env);
  };

  const [selectedProject, setSelectedProject] = useState<EnvSetting | null>(
    null
  );

  const authenticatedProject = selectedProject || {
    name: localStorage.getItem(TENANT_LOCALSTORAGE_KEY),
  };

  const onCompanySelected = () =>
    setEnvVariables(
      envSettings[selectedCompany.value.toLowerCase().trim()]
        .filter((x) => !activeDomain || x.domains.includes(activeDomain))
        ?.sort((a, b) => (a.name < b.name ? -1 : 1))
    );

  useEffect(() => {
    setSelectedProject(
      JSON.parse(
        localStorage.getItem(NEW_LOGIN_STORAGE_KEY) || '""'
      ) as EnvSetting
    );
  }, []);

  return (
    <div>
      {!props.isAuthenticated && (
        <>
          <Title level={5} style={{ marginBottom: 4 }}>
            Company
          </Title>
          <CreatableSelect
            value={selectedCompany}
            onChange={(e) => {
              setSelectedCompany(e!);
            }}
            options={companies}
          />
          <StyledButton
            style={{ marginTop: 10 }}
            htmlType="button"
            onClick={onCompanySelected}
            type="primary"
          >
            {props.isInitializing ? (
              <Icon
                type="Loader"
                style={{
                  marginRight: 10,
                  color: Colors['decorative--grayscale--700'],
                }}
              />
            ) : null}
            Continue
          </StyledButton>
          {props.hasError && (
            <p>
              An error occurred when logging into the environment above. Make
              sure you are a user that belongs in an Azure tenant that has the
              cluster installed.
            </p>
          )}
          <ProjectWrapper>
            {!envVariables
              ? `There is no setting for this name, make sure you entered it correctly or use the custom login UI.`
              : envVariables?.map((project) => (
                  <Project
                    htmlType="button"
                    disabled={props.isInitializing}
                    type="ghost"
                    onClick={() => handleSelectingProject(project)}
                    key={project.id}
                  >
                    {project.name}
                  </Project>
                ))}
          </ProjectWrapper>
        </>
      )}
      {props.isAuthenticated && (
        <>
          {authenticatedProject?.name ? (
            <>
              <HeaderPanel>
                <span>You are logged in: </span>
                <b>{authenticatedProject.name}</b>
              </HeaderPanel>
              <StyledButton
                style={{ marginBottom: 10 }}
                htmlType="button"
                onClick={() => props.move(authenticatedProject.name || '')}
                type="primary"
              >
                Continue
              </StyledButton>
              <StyledButton
                style={{ width: '100%' }}
                htmlType="button"
                onClick={props.logout}
                type="ghost"
              >
                Sign out
              </StyledButton>
            </>
          ) : (
            <p>You have used the advanced UI to login, please switch the UI.</p>
          )}
        </>
      )}
    </div>
  );
}

const Project = styled(Button)`
  margin-top: 5px;
  border: 1px solid hsl(0, 0%, 80%);
  border-radius: 4px;
  color: var(--cogs-text-color);
`;

const ProjectWrapper = styled.div`
  margin-top: 10px;
`;

const HeaderPanel = styled.div`
  text-align: center;
  margin-bottom: 10px;
`;

const StyledButton = styled(Button)`
  display: block;
  &.cogs-btn-disabled {
    color: #ffffff;
    background: var(--cogs-primary);
    opacity: 0.7;
    cursor: not-allowed !important;
    &:hover {
      background: var(--cogs-primary);
      color: #ffffff;
    }
  }
`;
